import { OutreachAction, SupplierThread } from '../../components/forms/outreach/types';

/**
 * Sample data for the /design step-4 gallery: one thread carrying four
 * actions, a counter anchor, a change ledger and a failed extraction; two
 * suppliers nobody has written to yet, each with an opening ask ready (one of
 * them missing a contact email); and two sent threads that can only be waited
 * on. That last split is what the row order is there to show — an ask the
 * buyer can send now outranks an email already sitting in someone's inbox.
 *
 * Outbound messages come in both shapes — a query with the sent email behind
 * its peek, and a legacy one with no query recorded. Fixtures only — never
 * used by the live step.
 */

export const hoursAgo = (h: number) => new Date(Date.now() - h * 3_600_000).toISOString();

const action = (over: Partial<OutreachAction> & Pick<OutreachAction, 'id' | 'supplierName' | 'type' | 'title'>): OutreachAction => ({
  rationale: '',
  suggestedAsk: '',
  priority: 2,
  status: 'OPEN',
  componentId: 'c-motor',
  ...over,
});

export const ACTIONS: OutreachAction[] = [
  action({
    id: 'a1',
    supplierName: 'Kaisen Motion Systems',
    threadId: 't-kaisen',
    type: 'NEGOTIATE_PRICE',
    title: 'Quoted ₹1,240 — 13% over target. Counter?',
    rationale: '13% over target, and under their own tier sheet',
    suggestedAsk: 'At 240 units we can commit at ₹1,060 per unit — can you meet that?',
    priority: 1,
    anchor: {
      price: 1060,
      currency: 'INR',
      floor: 980,
      ceiling: 1240,
      working: 'Market band ₹980–1,150 · another supplier ₹1,090 · their own 240-unit tier ₹1,120',
      clamped: false,
    },
  }),
  action({
    id: 'a2',
    supplierName: 'Kaisen Motion Systems',
    threadId: 't-kaisen',
    type: 'CLARIFY',
    title: 'Hold the price to 5 September',
    rationale:
      'Their price expires 29 Aug and your PO lands 2 Sep, so the quote you are comparing will already be void on the day you place the order.',
    suggestedAsk: 'Can you hold this price until 5 September?',
  }),
  action({
    id: 'a3',
    supplierName: 'Kaisen Motion Systems',
    threadId: 't-kaisen',
    type: 'ASK_LEAD_TIME_SPLIT',
    title: 'Take the 120-unit release at week 10',
    rationale: 'They offered the split themselves this morning.',
    suggestedAsk: 'We will take the 120-unit release at week 10.',
    priority: 3,
  }),
  action({
    id: 'a4',
    supplierName: 'Kaisen Motion Systems',
    threadId: 't-kaisen',
    type: 'REQUEST_FORMAL_QUOTE',
    title: 'Ask for the signed quotation',
    rationale: 'The PDF they sent is unsigned.',
    suggestedAsk: 'Please send the signed quotation.',
    priority: 4,
  }),
  action({
    id: 'a5',
    supplierName: 'Volans Composites',
    threadId: 't-volans',
    componentId: 'c-prop',
    type: 'VERIFY_COMPLIANCE',
    title: 'Compliance unconfirmed — verify before you shortlist',
    rationale: 'No certificate has been shown yet.',
    suggestedAsk: 'Please share your ISO certificate.',
  }),
  // Four days of silence, so this one is past its delay and the API returns
  // it. It still sits below every supplier with an unsent ask: chasing an
  // email is waiting, not doing.
  action({
    id: 'a6',
    supplierName: 'Sundara Precision Drives',
    threadId: 't-sundara',
    type: 'FOLLOW_UP',
    title: 'Silent four days — chase the quote?',
    rationale: 'Nothing back since the inquiry went out on Monday.',
    suggestedAsk: 'Following up on our inquiry — any update?',
    priority: 1,
  }),
  // Kickoff asks: no thread to attach to, matched to their supplier by
  // component + name. These are what makes an un-started row actionable.
  action({
    id: 'a7',
    supplierName: 'Shenzhen Kinetix',
    type: 'REQUEST_FORMAL_QUOTE',
    title: 'Opening ask ready — price at 240 units + lead time',
    rationale: 'They publish no pricing, so the first ask is the quote itself.',
    suggestedAsk: 'Please quote unit price at 240 units, with the lead time that price assumes.',
  }),
  action({
    id: 'a8',
    supplierName: 'Auriga Drivetrain',
    componentId: 'c-prop',
    type: 'VERIFY_COMPLIANCE',
    title: 'Opening ask ready — balance spec + price at 480 pairs',
    rationale: 'Compliance is what eliminates propeller suppliers late — ask now, not after shortlisting.',
    suggestedAsk: 'Please share the balance tolerance you hold to and unit price at 480 pairs.',
  }),
  action({
    id: 'a9',
    supplierName: 'Auriga Drivetrain',
    componentId: 'c-prop',
    type: 'REQUEST_FORMAL_QUOTE',
    title: 'Ask for the lead time behind the price',
    rationale: 'They publish a price band but never the weeks behind it, and the band is useless without them.',
    suggestedAsk: 'What lead time does that price assume at 480 pairs?',
    priority: 3,
  }),
];

export const THREADS: SupplierThread[] = [
  {
    id: 't-kaisen',
    componentId: 'c-motor',
    supplierName: 'Kaisen Motion Systems',
    hasContactEmail: true,
    componentName: 'Brushless DC motor',
    quantity: 240,
    targetPrice: 1100,
    targetPriceLabel: '₹1,100 / unit',
    status: 'replied',
    summary: 'Revised offer: ₹1,240/unit at 240 pcs, 14 weeks.',
    lastActivityIso: hoursAgo(3),
    quote: { unitPrice: 1240, currency: 'INR', leadTimeWeeks: 14, moq: 100, quotedAtIso: hoursAgo(3), rounds: 2 },
    changeHistory: [
      { field: 'QUOTED_PRICE', previousValue: '1310', newValue: '1240', sourceMessageId: 'm2', condition: null },
      { field: 'LEAD_TIME', previousValue: '16', newValue: '14', sourceMessageId: 'm2', condition: 'if confirmed by Friday' },
    ],
    commitments: [
      { type: 'PARTIAL_SHIPMENT', statement: '120 pcs can release at week 10', sourceMessageId: 'm2' },
      { type: 'VALIDITY', statement: 'Price valid until 29 August', sourceMessageId: 'm2' },
    ],
    extractionStatus: 'OK',
    messages: [
      {
        id: 'm1',
        sender: 'buyer',
        senderName: 'You',
        timestamp: 'Wed 11:02',
        sourceQuery:
          'Sixteen weeks lands after our build slot closes. At 240 units, can you look again at price and lead time?',
        text:
          'Subject: Brushless DC motor — 240 units, revised price and lead time\n\n' +
          'Hello Kaisen Motion Systems team,\n\n' +
          'We are finalising sourcing for a brushless DC motor at a volume of 240 units. Your current lead time of sixteen weeks falls outside our build window.\n\n' +
          'Could you review both unit price and lead time at this volume and confirm the best terms you can hold?\n\n' +
          'Kind regards,\nRobbie, on behalf of the buyer',
      },
      {
        id: 'm2',
        sender: 'supplier',
        senderName: 'Kaisen Motion Systems',
        timestamp: 'today 06:48',
        text: 'Revised offer: ₹1,240/unit at 240 pcs, 14 weeks. Price valid until 29 August. We can release 120 pcs at week 10 if the schedule is tight.',
        attachments: [{ filename: 'Kaisen_Quote_4008_240pcs.pdf', parseStatus: 'PARSED' }, { filename: 'Tier_pricing.xlsx', parseStatus: 'PARSED' }],
      },
    ],
  },
  {
    id: 't-volans',
    componentId: 'c-prop',
    supplierName: 'Volans Composites',
    hasContactEmail: true,
    componentName: 'Carbon-fibre propeller',
    quantity: 480,
    status: 'replied',
    summary: 'Sent 5 attachments: Balance_report.pdf, Scan_certificate.tif, Quote.zip +2 more',
    lastActivityIso: hoursAgo(6),
    extractionStatus: 'FAILED',
    messages: [
      {
        id: 'm3',
        sender: 'supplier',
        senderName: 'Volans Composites',
        timestamp: 'today 04:10',
        text: 'PFA.',
        // The "PFA" reply, and every chip state in one go: read, three ways of unreadable, and an older one with no parse result at all.
        attachments: [
          { filename: 'Balance_report.pdf', parseStatus: 'PARSED' }, { filename: 'Scan_certificate.tif', parseStatus: 'UNSUPPORTED' },
          { filename: 'Quote.zip', parseStatus: 'TOO_LARGE' }, { filename: 'Signature.docx', parseStatus: 'EMPTY' }, { filename: 'Legacy.pdf' },
        ],
      },
    ],
  },
  {
    id: 't-sundara',
    componentId: 'c-motor',
    supplierName: 'Sundara Precision Drives',
    hasContactEmail: true,
    componentName: 'Brushless DC motor',
    quantity: 240,
    status: 'awaiting_reply',
    summary: 'Inquiry sent — November delivery date led with',
    lastActivityIso: hoursAgo(96),
    // Sent before queries were recorded: no query to show, so the bubble is
    // the email — the peek still opens it as the record it is.
    messages: [
      {
        id: 'm4',
        sender: 'buyer',
        senderName: 'You',
        timestamp: 'Mon 09:12',
        text: 'Subject: Request for quotation — brushless DC motor\n\nHello Sundara Precision Drives team,\n\nWe are requesting a quotation for 240 brushless DC motors, including lead time and MOQ.\n\nKind regards,\nRobbie, on behalf of the buyer',
      },
    ],
  },
  {
    id: 't-helix',
    componentId: 'c-prop',
    supplierName: 'Helix Aero Components',
    hasContactEmail: true,
    componentName: 'Carbon-fibre propeller',
    quantity: 480,
    status: 'awaiting_reply',
    summary: '"Received — our quote team will revert by Friday."',
    lastActivityIso: hoursAgo(48),
    messages: [{ id: 'm5', sender: 'supplier', senderName: 'Helix Aero Components', timestamp: '2d ago', text: 'Received, thank you.' }],
  },
  {
    id: 't-kinetix',
    componentId: 'c-motor',
    supplierName: 'Shenzhen Kinetix',
    hasContactEmail: false,
    componentName: 'Brushless DC motor',
    quantity: 240,
    status: 'not_sent',
    summary: 'I haven’t asked them anything yet',
    messages: [],
  },
  {
    id: 't-auriga',
    componentId: 'c-prop',
    supplierName: 'Auriga Drivetrain',
    hasContactEmail: true,
    specMismatches: [{ key: 'diameter', label: 'Diameter', requested: '10in', offered: '9in', kind: 'different' }],
    specDecision: null,
    componentName: 'Carbon-fibre propeller',
    quantity: 480,
    status: 'not_sent',
    summary: 'I haven’t asked them anything yet',
    messages: [],
  },
];

/**
 * The step one send later: that supplier's ask has landed, so their thread is
 * now waiting and their open action is closed. Live sorting would drop the row
 * to the bottom of the list the moment this happens — which is precisely what
 * the gallery uses it to show NOT happening.
 */
export const afterSendingTo = (
  supplierName: string,
): { threads: SupplierThread[]; actions: OutreachAction[] } => ({
  threads: THREADS.map((thread) =>
    thread.supplierName === supplierName
      ? {
          ...thread,
          status: 'awaiting_reply' as const,
          summary: 'Ask sent — waiting on them',
          lastActivityIso: hoursAgo(0),
        }
      : thread,
  ),
  actions: ACTIONS.filter((action) => action.supplierName !== supplierName),
});

/**
 * A shortlisted supplier nobody has written to and Robbie has no ask for —
 * yet. Deliberately absent from ACTIONS: this is the thread whose body is
 * either a loader or the honest empty line, and never both.
 */
export const AWAITING_KICKOFF_THREAD: SupplierThread = {
  id: 't-nordwind',
  componentId: 'c-motor',
  supplierName: 'Nordwind Servo',
  hasContactEmail: true,
  componentName: 'Brushless DC motor',
  quantity: 240,
  targetPriceLabel: '₹1,100 / unit',
  status: 'not_sent',
  summary: 'I haven’t asked them anything yet',
  messages: [],
};
